import { useState, useEffect, useMemo, Suspense } from "react";
import { useSelector } from "react-redux";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";

// Material Dashboard 2 React example components
import Sidenav from "components/Sidenav";

// Material Dashboard 2 React themes
import theme from "assets/theme";

// Material Dashboard 2 React Dark Mode themes
import themeDark from "assets/theme-dark";

// Material Dashboard 2 React routes
import routes from "routes";

// Material Dashboard 2 React contexts
import { useMaterialUIController, setMiniSidenav } from "context/index";

// Images
import brandWhite from "assets/images/logo.png";
import brandMin from "assets/images/logo_min.png";
import useTokenChecker from "helpers/useTokenChecker";
import Loading from "components/Loading";
import { ToastContainer } from "react-toastify";
import CheckAuthRP from "./components/CheckAuthRP";
import "react-toastify/dist/ReactToastify.css";

export default function App() {
  useTokenChecker();

  const { role, user } = useSelector((state) => state.authentication);
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, direction, layout, sidenavColor, darkMode } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const { pathname } = useLocation();

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = () => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes
      .map((route) => {
        if (route.collapse) {
          return getRoutes(route.collapse);
        }

        if (route.route) {
          return (
            <Route exact path={route.route} element={<CheckAuthRP {...route} />} key={route.key} />
          );
        }

        return null;
      })
      .filter((r) => r);

  const availableRoutes = useMemo(() => {
    if (role) {
      return routes.filter((r) => !r.roles || (Array.isArray(r.roles) && r.roles.includes(role)));
    }
    return routes;
  }, [role]);
  return !role && user ? (
    <Loading />
  ) : (
    <ThemeProvider theme={darkMode ? themeDark : theme}>
      <CssBaseline />
      {layout === "dashboard" && (
        <>
          <Sidenav
            color={sidenavColor}
            brand={miniSidenav ? brandMin : brandWhite}
            routes={availableRoutes}
            onMouseEnter={handleOnMouseEnter}
            onMouseLeave={handleOnMouseLeave}
          />
        </>
      )}
      <Suspense fallback={null}>
        <Routes>
          {getRoutes(availableRoutes)}
          <Route path="*" element={<Navigate to="/dashboard" />} />
        </Routes>
      </Suspense>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </ThemeProvider>
  );
}
